import { useCallback, useEffect, useState } from 'react';
import { getLoyaltyDiscountLog } from '../services/reportsApi';
import type { DiscountPerformanceSource } from './useDiscountPerformance';

/* ═══════════════════════════════════════════════════════════════════════════
   Discount Log (live)
   GET /sales/loyalty/discount-log?locationId=&startDate=&endDate=&source=
       &orderSource=&search=&sortBy=&sortOrder=&page=&size=

   Order-level rows under the Discount & Promotion Performance card. Unlike the
   breakdown this is BACKEND paginated and sorted — the page, sort and source
   filter all round-trip, so nothing is sliced client-side.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface DiscountLogItem {
    orderNo: string;
    orderId: string;
    orderDate: string;
    orderTime: string;
    staff: string;
    customerName: string;
    source: DiscountPerformanceSource | string;
    discountName: string;
    /** Pre-composed badge label, e.g. "Loyalty · Redemption". */
    discount: string;
    amount: number;
}

export interface DiscountLogPage {
    content: DiscountLogItem[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export interface UseDiscountLogParams {
    locationId?: string | null;
    /** ISO yyyy-mm-dd. Omit both for the Today filter. */
    startDate?: string | null;
    endDate?: string | null;
    source?: DiscountPerformanceSource;
    orderSource?: 'direct' | 'thirdparty';
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** 1-based, as the endpoint expects. */
    page?: number;
    size?: number;
    token?: string | null;
}

const num = (v: any): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v: any): string => (v == null ? '' : String(v));

export function normalizeDiscountLog(res: any): DiscountLogPage {
    const body = res?.content ? res : (res?.data ?? res ?? {});
    const content: DiscountLogItem[] = Array.isArray(body?.content)
        ? body.content.map((r: any) => ({
            orderNo: str(r?.orderNo),
            orderId: str(r?.orderId),
            orderDate: str(r?.orderDate),
            orderTime: str(r?.orderTime),
            staff: str(r?.staff),
            customerName: str(r?.customerName).trim(),
            source: str(r?.source).toUpperCase(),
            discountName: str(r?.discountName),
            discount: str(r?.discount),
            amount: num(r?.amount),
        }))
        : [];
    return {
        content,
        page: num(body?.page),
        size: num(body?.size),
        totalElements: num(body?.totalElements),
        totalPages: num(body?.totalPages),
    };
}

/* Shares one in-flight promise across concurrent identical requests — see the
   note in useDiscountPerformance; StrictMode mounts twice in dev. Also used by
   the card's Export, which refetches the whole log on click. */
const inFlight = new Map<string, Promise<DiscountLogPage>>();

export function fetchDiscountLog(p: UseDiscountLogParams): Promise<DiscountLogPage> {
    const key = JSON.stringify([
        p.locationId, p.startDate, p.endDate, p.source, p.orderSource,
        p.search, p.sortBy, p.sortOrder, p.page, p.size, p.token,
    ]);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const req = getLoyaltyDiscountLog(p)
        .then(normalizeDiscountLog)
        .finally(() => { inFlight.delete(key); });
    inFlight.set(key, req);
    return req;
}

export function useDiscountLog({
    locationId, startDate, endDate, source, orderSource, search, sortBy, sortOrder, page, size, token,
}: UseDiscountLogParams) {
    const [data, setData] = useState<DiscountLogPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        // Dates are optional: omitting both is the Today (live) path.
        if (!locationId) { setData(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchDiscountLog({ locationId, startDate, endDate, source, orderSource, search, sortBy, sortOrder, page, size, token })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err: any) => {
                if (!cancelled) { setError(err?.message || 'Failed to load discount log'); setData(null); }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, source, orderSource, search, sortBy, sortOrder, page, size, token, reloadKey]);

    return { data, loading, error, refetch };
}

export default useDiscountLog;
